// src/utils/dateUtils.js

/**
 * Safely convert arbitrary input to a Date or null.
 * Accepts: Date, timestamp, ISO string, etc.
 */
export function toDate(input) {
  if (!input) return null

  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input
  }

  if (typeof input === 'number') {
    const d = new Date(input)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const s = String(input).trim()
  if (!s) return null

  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Parse expiry date from:
 * - "MMYY"   (e.g. "1226")
 * - "MM/YY"  (e.g. "12/26")
 * - any parsable Date / string
 * Returns a Date at the first day of that month, or null.
 */
export function parseExpiryDate(input) {
  if (!input) return null

  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input
  }

  let s = String(input).trim()
  if (!s) return null

  // Normalise separators
  s = s.replace(/[-.]/g, '/')

  // Match MM/YY
  const mmSlashYY = s.match(/^(\d{1,2})\/(\d{2})$/)
  if (mmSlashYY) {
    const mm = parseInt(mmSlashYY[1], 10)
    const yy = parseInt(mmSlashYY[2], 10)
    if (mm < 1 || mm > 12) return null
    const fullYear = 2000 + yy
    return new Date(fullYear, mm - 1, 1)
  }

  // Match MMYY
  if (/^\d{4}$/.test(s)) {
    const mm = parseInt(s.slice(0, 2), 10)
    const yy = parseInt(s.slice(2, 4), 10)
    if (mm < 1 || mm > 12) return null
    const fullYear = 2000 + yy
    return new Date(fullYear, mm - 1, 1)
  }

  // Fallback: let JS parse (for full dates / ISO)
  let d = new Date(s)
  if (Number.isNaN(d.getTime())) {
    // Check if DD/MM/YYYY format
    const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (dmy) {
      const day = parseInt(dmy[1], 10)
      const month = parseInt(dmy[2], 10)
      const year = parseInt(dmy[3], 10)
      if (month >= 1 && month <= 12) {
        d = new Date(year, month - 1, day)
      }
    }
  }
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Format expiry for display, using parseExpiryDate under the hood.
 * e.g. "12/26" -> "Dec 2026"
 */
export function formatExpiryDate(input, locale = 'en-IN') {
  const d = parseExpiryDate(input)
  if (!d) return 'N/A'
  return d.toLocaleDateString(locale, {
    month: 'short',
    year: 'numeric'
  })
}

/**
 * Validate expiry (MMYY or MM/YY or any parseable value).
 * Returns true only if parseExpiryDate succeeds.
 */
export function validateExpiry(input) {
  return !!parseExpiryDate(input)
}

/**
 * Check if an expiry is within N months from today (default 3).
 */
export function isExpirySoon(input, monthsAhead = 3) {
  const expiry = parseExpiryDate(input)
  if (!expiry) return false

  const now = new Date()
  const threshold = new Date(
    now.getFullYear(),
    now.getMonth() + monthsAhead,
    now.getDate()
  )

  return expiry <= threshold && expiry > now
}

/**
 * Format expiry date to MM/YY format (e.g. "12/26" for Dec 2026).
 */
export function formatExpiryToMMYY(input) {
  const d = parseExpiryDate(input)
  if (!d) return ''
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  return month + '/' + year
}

/**
 * Format expiry date to MMYY format without slash (e.g. "1226" for Dec 2026).
 * Use this for backend compatibility where slash-less format is required.
 */
export function formatExpiryToMMYYCompact(input) {
  const d = parseExpiryDate(input)
  if (!d) return ''
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  return month + year
}

